import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  IconButton,
  Text,
  Collapse,
  Portal,
  useDisclosure,
  Flex,
  Badge,
  Avatar,
  Icon,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FiMenu,
  FiX,
  FiHome,
  FiUsers,
  FiUser,
  FiLogOut,
  FiChevronRight,
  FiGrid,
  FiDatabase,
  FiExternalLink,
  FiSun,
  FiMoon,
  FiBookOpen,
  FiSearch,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginWithSmsModal } from '../pages/authentication';
import { getColor } from '../brandConfig';
import { useShiftKey, isDevMode } from '../hooks/useShiftKey';
import { renderPermissionTooltip } from './devPermissionTooltip';
import { useDraggableFloat, computeMenuPlacement } from '../hooks/useDraggableFloat';
import { useHideToEdge } from '../hooks/useHideToEdge';
import { RESET_FLOATING_PANELS } from '../hooks/floatingPanels';
import {
  LAUNCHER,
  LauncherEdgeTab,
  LauncherHideButton,
  launcherFabMotion,
  useLauncherReducedMotion,
  useLauncherClickOutside,
  menuMaxH,
} from './floating/LauncherChrome';

// Import module configs
import profileModuleConfig from '../pages/profile/moduleConfig';
import clientsModuleConfig from '../pages/clients/moduleConfig';
import mediaCDNModuleConfig from '../pages/media-cdn/moduleConfig';
import seoMetadataModuleConfig from '../pages/seometadata/moduleConfig';
import shelvModuleConfig from '../pages/shelv/moduleConfig';
import { useNavbarState } from '../hooks/useNavbarState';

// Animations
const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

// (the `float` idle-animation keyframes were removed with the animation itself —
//  no self-started motion on a launcher, see draggable-floating-launchers.md #12)

// Icon mapping for modules
const moduleIcons: Record<string, any> = {
  'shelv': FiBookOpen,
  profile: FiUser,
  clients: FiUsers,
  'media-cdn': FiDatabase,
  seometadata: FiSearch,
};

interface FloatingNavbarProps {
  modules?: any[]; // Resolved modules from App.tsx with backend permissions
}

/**
 * The launcher z-index ladder.
 *
 * 1101 = the FAB and its edge tab · 1102 = the menu panel.
 *
 * ⚠️ These were 998/999, one below Chakra's `zIndices.sticky` of **1100** — the
 * value the site navbar uses. So a launcher dragged to the top of the page went
 * UNDERNEATH the navbar and stopped responding to clicks entirely: the element
 * was there, visible through nothing, and `elementFromPoint` at its centre
 * returned a nav link. Verified in a real browser 2026-08-30.
 *
 * The standard's stated intent is "deliberately below the 1300 immersive
 * overlays" — a full-screen page is meant to cover the launcher. It never said
 * below the page's own chrome. 1101/1102 keeps the intent and closes the gap.
 */
// All available module configs
const allModuleConfigs = [
  shelvModuleConfig,
  profileModuleConfig,
  clientsModuleConfig,
  mediaCDNModuleConfig,
  seoMetadataModuleConfig,
];

const LAUNCHER_Z_FAB = 1101;
const LAUNCHER_Z_MENU = 1102;

export const FloatingNavbar: React.FC<FloatingNavbarProps> = ({ modules: resolvedModules }) => {
  // Use persistent state management with client-specific storage
  const { isAuthenticated, user, logout, refreshAuth } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  // Use client ID for user-specific navbar state
  const clientId = user?.clientId || user?.client?.id || user?.id;


  const {
    isExpanded,
    setIsExpanded,
    activeCategory,
    setActiveCategory,
    expandedModules,
    toggleModuleExpanded,
    resetState,
  } = useNavbarState(clientId);


  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();

  // Dev-mode Shift+hover permission reveal — see vault/how-to/dev-mode-permission-hover.md
  const shiftHeld = useShiftKey();
  const devReveal = isDevMode() && shiftHeld;

  // ── Launcher chrome (vault/how-to/draggable-floating-launchers.md) ─────
  // Per-user keys with a registered prefix. The template ships the neutral
  // `tenant_` prefix; localStorage is per-origin so tenants never collide.
  const navUserId = (user as any)?.id || 'guest';
  const posKey = `shelv_floating_navbar_pos_${navUserId}`;
  const hiddenKey = `shelv_floating_navbar_hidden_${navUserId}`;

  const { position, setPosition, dragHandlers, wasJustDragged } = useDraggableFloat({
    storageKey: posKey,
    size: LAUNCHER.FAB_SIZE,
    edgePad: LAUNCHER.EDGE_PAD,
    // Bottom-left is this launcher's slot on the default-corner map — the
    // module-nav launcher takes the left edge at vertical centre, so the two
    // never stack on a first run.
    defaultPosition: (vw, vh) => ({ left: 20, top: vh - LAUNCHER.FAB_SIZE - 20 }),
  });
  const { hidden, setHidden, justHidden } = useHideToEdge(hiddenKey);
  const reducedMotion = useLauncherReducedMotion();

  // The reset failsafe: snap home AND clear our own keys, so the footer's
  // "↻ Reset floating panels" needs no knowledge of which floaters exist.
  useEffect(() => {
    const onReset = () => {
      setIsExpanded(false);
      setHidden(false);
      try {
        localStorage.removeItem(posKey);
        localStorage.removeItem(hiddenKey);
      } catch { /* noop */ }
      setPosition({ left: 20, top: window.innerHeight - LAUNCHER.FAB_SIZE - 20 });
    };
    window.addEventListener(RESET_FLOATING_PANELS, onReset);
    return () => window.removeEventListener(RESET_FLOATING_PANELS, onReset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posKey, hiddenKey, setPosition]);

  // pointerdown, not mousedown — a finger tap outside is a pointer event first.
  useLauncherClickOutside(isExpanded, ['.floating-navbar-fab', '.floating-navbar-menu'], () => setIsExpanded(false));

  // The menu lives in its OWN Portal and is placed against the VIEWPORT, never
  // anchored to the FAB's container — see § Menu placement, the container-anchor trap.
  // Viewport in STATE, not read during render: `window.innerHeight` read inline
  // never re-runs on resize or an orientation change, so the menu keeps the
  // placement it was given at mount and can end up off-screen after a rotate.
  const [viewport, setViewport] = useState(() =>
    typeof window !== 'undefined'
      ? { w: window.innerWidth, h: window.innerHeight }
      : { w: 1200, h: 800 }
  );
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const vh = viewport.h;

  const placement = computeMenuPlacement(
    { left: position.left, top: position.top, size: LAUNCHER.FAB_SIZE },
    viewport,
    {
      preferredWidth: LAUNCHER.MENU_W,
      // Only steers WHICH SIDE the menu opens on; menuMaxH is the real cap, so
      // a rough figure is correct here and avoids depending on the module list
      // (declared below) just to place a box.
      estimatedHeight: Math.min(LAUNCHER.MENU_CAP, vh - LAUNCHER.EDGE_PAD * 2),
      gap: LAUNCHER.MENU_GAP,
    },
    LAUNCHER.EDGE_PAD,
  );

  // Refresh auth data when component mounts and user is authenticated but data is missing
  useEffect(() => {

    if (isAuthenticated && (!user || !user.permissions)) {
      refreshAuth();
    }
  }, [isAuthenticated, user, refreshAuth]);


  // Merge resolved permissions from backend (via App.tsx) into internal module list.
  // Modules migrated to single-source-of-truth have empty permission arrays in their config
  // and get real permissions resolved from the backend at runtime.
  const mergedModuleConfigs = React.useMemo(() => {
    if (!resolvedModules) return allModuleConfigs;
    const resolvedMap = new Map(resolvedModules.map((m: any) => [m.id, m]));
    return allModuleConfigs.map(mod => {
      const resolved = resolvedMap.get(mod.id);
      if (!resolved) return mod; // Not in App.tsx's list — keep as-is
      return { ...mod, permissions: resolved.permissions, navigation: resolved.navigation };
    });
  }, [resolvedModules]);

  // Filter modules based on user permissions
  const getAvailableModules = () => {
    // console.log('📦 [4] FloatingNavbar - Getting available modules', {
    //   totalModules: allModuleConfigs.length,
    //   hasUser: !!user,
    //   userPermissions: user?.permissions || [],
    // });

    if (!user || !user.permissions || user.permissions.length === 0) {
      return [];
    }

    const filteredModules = mergedModuleConfigs.filter(module => {
      const hasPermission = user?.permissions?.some((permission: string) => {
        // Check for view permissions in the standard format
        const hasViewPermission = module.permissions?.view?.includes(permission) || false;
        if (hasViewPermission) {
          // console.log('✅ [6] FloatingNavbar - Module accessible', {
          //   module: module.id,
          //   permission,
          //   viewPermissions: module.permissions.view,
          // });
        }
        return hasViewPermission;
      }) || false;

      return hasPermission;
    });


    return filteredModules;
  };

  // Group modules by category
  const categorizedModules = {
    'Kitchen': ['shelv'],
    'Site': ['clients', 'media-cdn', 'seometadata'],
    'Settings': ['profile'],
  };

  const availableModules = isAuthenticated ? getAvailableModules() : [];


  const handleNavItemClick = (path: string) => {
    navigate(path);
    // Keep the menu state but close the expanded view
    setIsExpanded(false);
  };

  const handleOpenInNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsExpanded(false);
    // Reset navbar state on logout
    resetState();
  };

  // Filter navigation items based on user permissions
  const getVisibleNavItems = (navigation: any) => {
    if (!navigation) return [];

    // Handle different navigation formats
    let navItems: any[] = [];
    if (Array.isArray(navigation)) {
      navItems = navigation;
    } else if (navigation.sub && Array.isArray(navigation.sub)) {
      navItems = navigation.sub;
    }

    return navItems.filter((item: any) => {
      // If no permissions required, show to everyone
      if (!item.permissions || item.permissions.length === 0) return true;

      // Check if user has required permission
      const hasPermission = user?.permissions?.some((permission: string) =>
        item.permissions.includes(permission)
      ) || false;

      // Debug logging for merge clients
      if (item.path === '/clients/merge') {
        console.log('🔍 [NAV-DEBUG] Merge Clients check:', {
          itemPermissions: item.permissions,
          userPermissions: user?.permissions,
          hasPermission
        });
      }

      return hasPermission;
    });
  };

  // NOTE: the old hand-rolled click-outside listener that lived here was removed
  // 2026-08-28. It exempted `.floating-navbar-container` — correct while the menu
  // was a child of the FAB's container, and WRONG the moment the menu moved into
  // its own Portal as `.floating-navbar-menu`. It fired on `mousedown`, so every
  // click on a nav item closed the menu BEFORE the click could land: the menu
  // appeared to do nothing. `useLauncherClickOutside` above supersedes it and
  // exempts both the FAB and the menu, on `pointerdown` (a finger tap is a
  // pointer event first — see the standard).
  //
  // Closing the menu also clears the open category.
  useEffect(() => {
    if (!isExpanded) setActiveCategory(null);
  }, [isExpanded, setActiveCategory]);

  // While hidden, ONLY the edge tab renders — never nothing, never gated.
  if (hidden) {
    return (
      <Portal>
        <LauncherEdgeTab
          edge="left"
          top={position.top}
          bg={getColor("secondary", colorMode)}
          label="Bring the navigation menu back"
          onClick={() => setHidden(false)}
          zIndex={LAUNCHER_Z_FAB}
          announce={justHidden}
        />
      </Portal>
    );
  }

  return (
    <>
      {/* The MENU — its own Portal, placed against the viewport by
          computeMenuPlacement. Anchoring it to the FAB's container is the trap
          documented in § Menu placement; a dragged FAB would push the menu
          off-screen. */}
      {isExpanded && (
        <Portal>
          <Box
            className="floating-navbar-menu"
            position="fixed"
            // `computeMenuPlacement` returns EITHER `top` (opening down) or
            // `bottom` (opening up) — NEVER both. Reading only `top` renders
            // `top="undefinedpx"`: invalid CSS, so the browser drops the offset
            // and the menu lands off-screen — the launcher looks like it simply
            // does not open. That happened whenever the FAB sat near the bottom
            // of the viewport, which is its DEFAULT position. Master always
            // spread both; this file lost it in the 2026-08-28 rewrite.
            {...(placement.top !== undefined ? { top: `${placement.top}px` } : {})}
            {...(placement.bottom !== undefined ? { bottom: `${placement.bottom}px` } : {})}
            left={`${placement.left}px`}
            width={`${placement.width}px`}
            maxH={menuMaxH(placement)}
            overflowY="auto"
            overscrollBehavior="contain"
            zIndex={LAUNCHER_Z_MENU}
          >
            <Box
              bg={colorMode === 'light'
                ? "rgba(255, 255, 255, 0.98)"
                : "rgba(10, 10, 15, 0.95)"}
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              border="1px solid"
              borderColor={getColor("border.light", colorMode)}
              p={4}
              mb={4}
              minW="320px"
              maxW="400px"
              maxH="70vh"
              overflowY="auto"
              boxShadow={colorMode === 'light'
                ? "0 10px 40px rgba(0, 0, 0, 0.15)"
                : "0 20px 60px rgba(0, 0, 0, 0.8)"}
              animation={`${slideIn} 0.3s ease-out`}
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: getColor("secondary", colorMode),
                  borderRadius: '2px',
                },
              }}
            >
              {/* Theme Toggle Button - Always visible */}
              <Flex justify="flex-end" mb={3}>
                <IconButton
                  aria-label="Toggle color mode"
                  icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
                  size="sm"
                  variant="ghost"
                  color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                  onClick={toggleColorMode}
                  _hover={{
                    bg: colorMode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                    transform: 'scale(1.1)',
                  }}
                />
              </Flex>

              {/* User Profile Section */}
              {isAuthenticated && user && (
                <Flex
                  align="center"
                  justify="space-between"
                  mb={4}
                  p={3}
                  bg={colorMode === 'light' ? "rgba(90, 200, 250, 0.05)" : "rgba(90, 200, 250, 0.1)"}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'light' ? "rgba(90, 200, 250, 0.1)" : "rgba(90, 200, 250, 0.2)"}
                >
                  <HStack
                    spacing={3}
                    cursor="pointer"
                    onClick={() => handleNavItemClick('/profile')}
                    _hover={{ opacity: 0.8 }}
                    transition="opacity 0.2s"
                  >
                    <Avatar
                      size="sm"
                      name={`${user.fName || user.firstName || ""} ${user.lName || user.lastName || ""}`}
                      src={user.profilePhoto || undefined}
                      bg={getColor("secondary", colorMode)}
                    />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="600" color={colorMode === 'light' ? "gray.700" : "white"}>
                        {user.fName || user.firstName || 'User'} {user.lName || user.lastName || ''}
                      </Text>
                      <Text fontSize="xs" color={colorMode === 'light' ? "gray.500" : "gray.400"}>
                        {user.email}
                      </Text>
                    </VStack>
                  </HStack>
                  <IconButton
                    aria-label="Logout"
                    icon={<FiLogOut />}
                    size="sm"
                    variant="ghost"
                    color="red.400"
                    onClick={handleLogout}
                    _hover={{
                      bg: 'rgba(255, 0, 0, 0.1)',
                      transform: 'scale(1.1)',
                    }}
                  />
                </Flex>
              )}

              {/* Quick Actions */}
              <HStack mb={4} spacing={2}>
                <Button
                  size="sm"
                  leftIcon={<FiHome />}
                  bg={getColor("secondary", colorMode)}
                  color="white"
                  fontWeight="600"
                  borderRadius="full"
                  _hover={{
                    bg: getColor("secondaryHover", colorMode),
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.2s"
                  onClick={() => {
                    navigate('/');
                    setIsExpanded(false);
                  }}
                >
                  Home
                </Button>
                {!isAuthenticated && (
                  <Button
                    size="sm"
                    bg={getColor("secondary", colorMode)}
                    color="white"
                    fontWeight="600"
                    borderRadius="full"
                    onClick={onLoginOpen}
                    _hover={{
                      bg: getColor("secondaryHover", colorMode),
                      transform: 'translateY(-2px)',
                    }}
                    transition="all 0.2s"
                  >
                    Sign In
                  </Button>
                )}
              </HStack>

              {/* Module Categories */}
              {isAuthenticated && (
                <VStack spacing={2} align="stretch">
                  {Object.entries(categorizedModules).map(([category, moduleIds]) => {
                    const categoryModules = availableModules.filter(m => 
                      moduleIds.includes(m.id)
                    );
                    
                    if (categoryModules.length === 0) return null;

                    return (
                      <Box key={category}>
                        <Button
                          size="sm"
                          variant="ghost"
                          color={colorMode === 'light' ? "gray.600" : "gray.400"}
                          fontSize="xs"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          justifyContent="space-between"
                          w="full"
                          onClick={() => {
                            const newCategory = activeCategory === category ? null : category;
                            setActiveCategory(newCategory);
                          }}
                          _hover={{
                            bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                            color: getColor("secondary", colorMode),
                          }}
                          rightIcon={
                            <Box
                              transform={activeCategory === category ? 'rotate(90deg)' : 'rotate(0)'}
                              transition="transform 0.2s"
                            >
                              <FiChevronRight />
                            </Box>
                          }
                        >
                          {category}
                        </Button>
                        
                        <Collapse in={activeCategory === category} animateOpacity>
                          <VStack spacing={1} align="stretch" mt={2} pl={4}>
                            {categoryModules.map((module) => {
                              const IconComponent = moduleIcons[module.id] || FiGrid;
                              const isModuleExpanded = expandedModules.has(module.id);
                              const visibleNavItems = getVisibleNavItems(module.navigation);
                              
                              // Get the main module path (handle different navigation formats)
                              let mainModulePath = `/module/${module.id}`;
                              if (module.navigation) {
                                if (Array.isArray(module.navigation) && module.navigation[0]) {
                                  mainModulePath = module.navigation[0].path;
                                } else if (!Array.isArray(module.navigation) && typeof module.navigation === 'object' && 'main' in module.navigation) {
                                  mainModulePath = (module.navigation as any).main.path;
                                }
                              } else if (module.routes?.[0]) {
                                mainModulePath = module.routes[0].path;
                              }
                              
                              // Permissions for the module-level button — use the first
                              // nav item's permissions since that's where the click lands.
                              const moduleButtonPerms: string[] =
                                (visibleNavItems[0]?.permissions as string[] | undefined) ?? [];
                              const moduleButtonLevel = visibleNavItems[0]?.permissionLevel as string | undefined;

                              return (
                                <Box key={module.id}>
                                  <HStack
                                    spacing={0}
                                    onMouseEnter={() => setHoveredItem(`module-${module.id}`)}
                                    onMouseLeave={() => setHoveredItem(null)}
                                  >
                                    <Tooltip
                                      label={
                                        devReveal
                                          ? renderPermissionTooltip({
                                              label: module.name,
                                              path: mainModulePath,
                                              moduleId: module.id,
                                              permissionLevel: moduleButtonLevel,
                                              resolved: moduleButtonPerms,
                                            })
                                          : module.name
                                      }
                                      placement="right"
                                      hasArrow
                                      isDisabled={!devReveal}
                                      bg={devReveal ? 'gray.800' : undefined}
                                      color={devReveal ? 'white' : undefined}
                                      px={devReveal ? 3 : undefined}
                                      py={devReveal ? 2 : undefined}
                                    >
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      color={colorMode === 'light' ? "gray.700" : "gray.300"}
                                      flex="1"
                                      justifyContent="flex-start"
                                      leftIcon={<Icon as={IconComponent} />}
                                      onClick={() => {
                                        // Navigate to the main module page
                                        handleNavItemClick(mainModulePath);
                                      }}
                                      position="relative"
                                      _hover={{
                                        bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                        color: getColor("secondary", colorMode),
                                        transform: 'translateX(4px)',
                                        _before: {
                                          opacity: 1,
                                        }
                                      }}
                                      _before={{
                                        content: '""',
                                        position: 'absolute',
                                        left: '-16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '3px',
                                        height: '70%',
                                        bg: `linear-gradient(180deg, transparent, ${getColor("secondary", colorMode)}, transparent)`,
                                        borderRadius: 'full',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                      }}
                                      transition="all 0.2s"
                                    >
                                      {module.name}
                                    </Button>
                                    </Tooltip>

                                    {/* Open in new tab button - shows on hover */}
                                    <IconButton
                                      aria-label="Open in new tab"
                                      icon={<FiExternalLink size="12" />}
                                      size="xs"
                                      variant="ghost"
                                      color="gray.400"
                                      opacity={hoveredItem === `module-${module.id}` ? 1 : 0}
                                      pointerEvents={hoveredItem === `module-${module.id}` ? 'auto' : 'none'}
                                      onClick={() => handleOpenInNewTab(mainModulePath)}
                                      _hover={{
                                        bg: 'rgba(90, 200, 250, 0.2)',
                                        color: getColor("secondary", colorMode),
                                      }}
                                      transition="all 0.2s"
                                    />
                                    
                                    {/* Expand/collapse button */}
                                    {visibleNavItems.length > 0 && (
                                      <IconButton
                                        aria-label="Expand module"
                                        icon={
                                          <Box
                                            transform={isModuleExpanded ? 'rotate(90deg)' : 'rotate(0)'}
                                            transition="transform 0.2s"
                                          >
                                            <FiChevronRight size="14" />
                                          </Box>
                                        }
                                        size="xs"
                                        variant="ghost"
                                        color="gray.400"
                                        onClick={() => {
                                          toggleModuleExpanded(module.id);
                                        }}
                                        _hover={{
                                          bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                          color: getColor("secondary", colorMode),
                                        }}
                                      />
                                    )}
                                  </HStack>
                                  
                                  {/* Sub-navigation items */}
                                  <Collapse in={isModuleExpanded} animateOpacity>
                                    <VStack spacing={0.5} align="stretch" mt={1} pl={8}>
                                      {visibleNavItems.map((navItem: any) => (
                                        <HStack
                                          key={navItem.path}
                                          spacing={0}
                                          onMouseEnter={() => setHoveredItem(`nav-${navItem.path}`)}
                                          onMouseLeave={() => setHoveredItem(null)}
                                        >
                                          <Tooltip
                                            label={
                                              devReveal
                                                ? renderPermissionTooltip({
                                                    label: navItem.label,
                                                    path: navItem.path,
                                                    moduleId: module.id,
                                                    permissionLevel: navItem.permissionLevel,
                                                    resolved: (navItem.permissions as string[]) ?? [],
                                                  })
                                                : navItem.label
                                            }
                                            placement="right"
                                            hasArrow
                                            isDisabled={!devReveal}
                                            bg={devReveal ? 'gray.800' : undefined}
                                            color={devReveal ? 'white' : undefined}
                                            px={devReveal ? 3 : undefined}
                                            py={devReveal ? 2 : undefined}
                                          >
                                          <Button
                                            size="xs"
                                            variant="ghost"
                                            color={colorMode === 'light' ? "gray.600" : "gray.400"}
                                            flex="1"
                                            justifyContent="flex-start"
                                            leftIcon={navItem.icon ? <Text fontSize="xs">{navItem.icon}</Text> : undefined}
                                            onClick={() => handleNavItemClick(navItem.path)}
                                            fontSize="xs"
                                            fontWeight="400"
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.1)',
                                              color: getColor("secondary", colorMode),
                                              transform: 'translateX(2px)',
                                            }}
                                            transition="all 0.15s"
                                          >
                                            {navItem.label}
                                            {navItem.badge && (
                                              <Badge
                                                ml={1}
                                                colorScheme={navItem.badge.color || 'gray'}
                                                fontSize="2xs"
                                                variant="subtle"
                                              >
                                                {navItem.badge.text}
                                              </Badge>
                                            )}
                                          </Button>
                                          </Tooltip>
                                          
                                          {/* Open in new tab button for sub-items */}
                                          <IconButton
                                            aria-label="Open in new tab"
                                            icon={<FiExternalLink size="10" />}
                                            size="xs"
                                            variant="ghost"
                                            color="gray.500"
                                            opacity={hoveredItem === `nav-${navItem.path}` ? 1 : 0}
                                            pointerEvents={hoveredItem === `nav-${navItem.path}` ? 'auto' : 'none'}
                                            onClick={() => handleOpenInNewTab(navItem.path)}
                                            minW="20px"
                                            h="20px"
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.2)',
                                              color: getColor("secondary", colorMode),
                                            }}
                                            transition="all 0.2s"
                                          />
                                        </HStack>
                                      ))}
                                    </VStack>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </VStack>
                        </Collapse>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </Box>
        </Portal>
      )}

      {/* The FAB — draggable, its own Portal, on the default-corner map. */}
      <Portal>
        <Box
          className="floating-navbar-container"
          position="fixed"
          left={`${position.left}px`}
          top={`${position.top}px`}
          zIndex={LAUNCHER_Z_FAB}
        >
          <LauncherHideButton
            fabSize={LAUNCHER.FAB_SIZE}
            label="Hide this menu — it becomes a tab on the edge of the screen"
            onClick={() => {
              setIsExpanded(false);
              setHidden(true);
            }}
          />
          <Tooltip
            hasArrow
            placement="right"
            openDelay={300}
            label="Open the navigation menu — drag to move it anywhere"
          >
            <IconButton
              className="floating-navbar-fab"
              aria-label="Toggle navigation"
              icon={isExpanded ? <FiX /> : <FiMenu />}
              w={`${LAUNCHER.FAB_SIZE}px`}
              h={`${LAUNCHER.FAB_SIZE}px`}
              minW={`${LAUNCHER.FAB_SIZE}px`}
              borderRadius="full"
              bg={getColor("secondary", colorMode)}
              color="white"
              border="2px solid"
              borderColor="rgba(255, 255, 255, 0.2)"
              {...dragHandlers}
              {...launcherFabMotion(reducedMotion)}
              onClick={() => {
                // A drag must never also register as a click.
                if (wasJustDragged()) return;
                setIsExpanded(!isExpanded);
              }}
              _hover={{ bg: getColor("secondaryHover", colorMode) }}
              position="relative"
            />
          </Tooltip>
        </Box>
      </Portal>

      {/* ===== Emergency logout mini-FAB (always visible when authenticated) =====
          Escape hatch for broken auth state: if permissions never load, `user` is null,
          or the menu won't open for any reason, tapping this immediately clears the
          session. Sits directly above the main FAB.
          See vault/how-to/draggable-floating-launchers.md → "Emergency logout mini-FAB".
          Upgraded to the draggable hook 2026-08-28, so it now TRACKS the dragged FAB
          (`position.top - 40 / position.left + 14`) as on PLCC / SurgReady, rather
          than the old fixed `bottom:20 left:20` anchor. It hides with the launcher —
          an escape hatch pinned to a corner the user has dragged away from is worse
          than no escape hatch. */}
      {isAuthenticated && !hidden && (
        <Portal>
          <Box
            className="floating-navbar-logout"
            position="fixed"
            top={`${position.top - 40}px`}
            left={`${position.left + 14}px`}
            zIndex={997}
          >
            <Tooltip
              hasArrow
              placement="right"
              openDelay={300}
              label="Emergency logout — always visible, bypasses the menu"
            >
              <IconButton
                aria-label="Emergency logout (always visible)"
                icon={<FiLogOut />}
                size="sm"
                borderRadius="full"
                bg="red.400"
                color="white"
                border="2px solid"
                borderColor="rgba(255, 255, 255, 0.3)"
                boxShadow="0 2px 8px rgba(255, 0, 0, 0.3)"
                opacity={0.85}
                onClick={handleLogout}
                _hover={{
                  opacity: 1,
                  bg: 'red.500',
                  transform: 'scale(1.15)',
                  boxShadow: '0 4px 12px rgba(255, 0, 0, 0.5)',
                }}
                transition="all 0.2s"
              />
            </Tooltip>
          </Box>
        </Portal>
      )}

      {/* Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={() => {
          onLoginClose();
          window.location.reload();
        }}
      />
    </>
  );
};